import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaArrowRight } from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

const WhatWeGetSection = () => {
    const sectionRef = useRef(null);
    
    // Animation Refs
    const titleRefs = useRef([]);      // For "What We Get" and Subtitle
    const listItemsRefs = useRef([]);  // For the 4 list points
    const buttonRef = useRef(null);    // For the Apply button
    const imageContainerRef = useRef(null); // Right column container
    const imageRef = useRef(null);     // The actual image inside

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%", // Animation starts when section is 75% down viewport
                    toggleActions: "play none none none",
                }
            });

            // 1. Titles Fade Up
            tl.from(titleRefs.current, {
                y: 40,
                opacity: 0,
                duration: 0.6,
                stagger: 0.2,
                ease: "power2.out"
            });

            // 2. Image Section Enters (Slide in from Right + Zoom Out effect)
            tl.from(imageContainerRef.current, {
                x: 60, // Slide from right
                opacity: 0,
                duration: 0.8,
                ease: "power2.out"
            }, "-=0.4"); // Overlap slightly with titles

            tl.from(imageRef.current, {
                scale: 1.3, // Start zoomed in
                duration: 1.4,
                ease: "power2.out"
            }, "<"); // Run at same time as container slide

            // 3. List Items Slide In (From Left)
            // This complements the Arrows pointing right
            tl.from(listItemsRefs.current, {
                x: -30, // Come from left
                opacity: 0,
                duration: 0.5,
                stagger: 0.15,
                ease: "power2.out"
            }, "-=0.8"); // Start this while image is still settling

            // 4. Button Pop
            tl.from(buttonRef.current, {
                scale: 0.8,
                opacity: 0,
                duration: 0.4,
                ease: "back.out(1.7)"
            }, "-=0.2");

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Helper to add refs safely
    const addToTitleRefs = (el) => { if (el && !titleRefs.current.includes(el)) titleRefs.current.push(el); };
    const addToListRefs = (el) => { if (el && !listItemsRefs.current.includes(el)) listItemsRefs.current.push(el); };

    return (
        <section ref={sectionRef} className="container max-w-7xl mx-auto px-6 md:px-12 my-[100px]">
            <div className="flex flex-row max-[800px]:flex-wrap-reverse gap-12">

                {/* Left Column: Text Content */}
                <div className="w-full space-y-10">
                    {/* Who We Are Section */}
                    <div className='flex flex-col py-3 gap-y-10'>
                        {/* Wrapper div for titles to help grouping if needed, or just apply refs directly */}
                        <div>
                            <h3 ref={addToTitleRefs} className='text-[#000000] font-[600] text-[34px] sm:text-[42px] leading-[48px]'>What We Get</h3>
                            <h5 ref={addToTitleRefs} className='text-[#000000] font-[600] text-[20px] sm:text-[28px] leading-[30px] mt-2'>We work on Sweat Equity +  Revenue Share</h5>
                        </div>

                        <ol className='flex flex-col gap-y-1'>
                            <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                                <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                                <p className='text-[#374151] text-[16px] font-[600]'>5–8% Sweat Equity Depending on Engagement.</p>
                            </li>
                            <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                                <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                                <p className='text-[#374151] text-[16px] font-[600]'>Retainer only for Extended Dev or Marketing Sprints.</p>
                            </li>
                            <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                                <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                                <p className='text-[#374151] text-[16px] font-[600]'>Commitment to Test, Iterate,and Scale in Sprints.</p>
                            </li>
                            <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                                <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                                <p className='text-[#374151] text-[16px] font-[600]'>We win only when you grow.</p>
                            </li>
                        </ol>
                    </div>

                    {/* CTA Button */}
                    <div ref={buttonRef}>
                        <a href='https://forms.gle/F1AhEsmWPnMmpXGM7' target='_blank' rel="noreferrer" className="inline-block bg-[#EA580C] hover:bg-orange-500 text-white py-3 px-8 rounded-full font-[600] transition-colors duration-300 text-sm">
                            Apply Now
                        </a>
                    </div>
                </div>

                {/* Right Column: Philosophy Card */}
                <div className="w-full max-[800px]:h-96">
                    <div ref={imageContainerRef} className="relative rounded-3xl overflow-hidden shadow-2xl h-full">
                        {/* Background Image and Gradient Overlay */}
                        <div className="absolute inset-0">
                            <img
                                ref={imageRef} 
                                src="img/what-we-get.png"
                                alt="Startup Philosophy Background"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/40 to-blue-900/80 mix-blend-multiply"></div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default WhatWeGetSection;